import React from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";

// MUI
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import HistoryIcon from "@material-ui/icons/History";

// Locals
import { firestoreQuery, localStorageKey } from "../../constants";
import { useUserId } from "../../authentication/hooks";
import { useData } from "../../firebase/hooks";
import useStyles from "../styles";

// Types
import type { SingleItemState } from "../../items/types";
import type { WsParams } from "../../routes";

const RecentlyViewedItems: React.FC = () => {
  const classes = useStyles();

  const { workspaceId } = useParams<WsParams>();
  const userId = useUserId();
  const items = useData<Record<string, SingleItemState>>(firestoreQuery.wsItems);

  const lastItems = React.useMemo<Array<{ id: string; name: string }>>(() => {
    if (items && userId) {
      const lastItemsString =
        localStorage.getItem(localStorageKey.lastItems(workspaceId, userId)) ?? "[]";
      const lastItems = JSON.parse(lastItemsString);
      return lastItems
        .filter((itemId: string) => items[itemId])
        .map((itemId: string) => ({
          id: itemId,
          name: items[itemId].name,
        }));
    }
    return [];
  }, [items, userId, workspaceId]);

  return (
    <>
      <Typography variant="h6" className={classes.cardTitle} data-cy="home-recently-viewed">
        Recently viewed items
      </Typography>

      {lastItems.length ? (
        <Box style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {lastItems.map((item, index) => (
            <Link
              key={item.id}
              to={`/ws/${workspaceId}/items/${item.id}`}
              style={{ textDecoration: "none" }}
            >
              <Paper
                elevation={1}
                style={{
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  backgroundColor: "#fafafa",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.12)";
                  e.currentTarget.style.borderColor = "#1976d2";
                  e.currentTarget.style.backgroundColor = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.12)";
                  e.currentTarget.style.borderColor = "#e0e0e0";
                  e.currentTarget.style.backgroundColor = "#fafafa";
                }}
              >
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: 500,
                    color: "#333",
                  }}
                >
                  {item.name}
                </Typography>
              </Paper>
            </Link>
          ))}
        </Box>
      ) : (
        <Box className={classes.emptyState}>
          <HistoryIcon className={classes.emptyStateIcon} />
          <Typography variant="body2" color="textSecondary">
            No recently viewed items
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
            Items you view will appear here
          </Typography>
        </Box>
      )}
    </>
  );
};

export default RecentlyViewedItems;
